import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Calendar } from 'lucide-react';
import FeeCollectionCharts from './FeeCollectionCharts';
import SecurityFeeAudit from './SecurityFeeAudit';
import SecurityFeeDashboard from './SecurityFeeDashboard';
import MonthlyTrends from './MonthlyTrends';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const DEFER_EXTRA_MS = 300;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const DashboardSecurityFee = () => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [loadExtras, setLoadExtras] = useState(false);

  useEffect(() => {
    setLoadExtras(false);
    const timer = setTimeout(() => setLoadExtras(true), DEFER_EXTRA_MS);
    return () => clearTimeout(timer);
  }, [appliedMonth, appliedYear, searchTrigger]);

  const handleSearch = () => {
    setAppliedMonth(month);
    setAppliedYear(year);
    setSearchTrigger((t) => t + 1);
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('dashboardSecurityFee.title')}</h1>
            <p className="text-gray-600 mt-2">{t('dashboardSecurityFee.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              <Search className="h-4 w-4" />
              {t('common.search')}
            </button>
          </div>
        </div>
      </div>

      {/* Security Fee Dashboard - Main component */}
      <div className="mb-8">
        <SecurityFeeDashboard appliedMonth={appliedMonth} appliedYear={appliedYear} />
      </div>

      {/* Monthly analysis trends — deferred so main summary loads first */}
      {loadExtras && (
        <div className="mb-8">
          <MonthlyTrends type="revenue" month={appliedMonth} year={appliedYear} />
        </div>
      )}

      {/* Fee Collection Charts — deferred so main summary loads first */}
      {loadExtras && (
        <div className="mb-8">
          <FeeCollectionCharts appliedMonth={appliedMonth} appliedYear={appliedYear} searchTrigger={searchTrigger} />
        </div>
      )}

      {/* Security Fee Audit — deferred */}
      {loadExtras && (
        <div className="mb-8">
          <SecurityFeeAudit appliedMonth={appliedMonth} appliedYear={appliedYear} searchTrigger={searchTrigger} />
        </div>
      )}
    </main>
  );
};

export default DashboardSecurityFee;